/**
 * Viewport: the only place where normalised field space meets pixels.
 *
 * The renderer letterboxes the surface into the canvas at the aspect ratio the
 * sport pack declares, so nothing downstream ever needs to know the sport's real
 * dimensions.
 */

import type { Vec2 } from '../core/index.ts';

/**
 * How the drawing is turned on screen, clockwise in degrees. At 90 the field's
 * left end is at the top; at -90 its right end is.
 */
export type ViewRotation = 0 | 90 | -90;

/**
 * Everything but `rotation` describes the *drawing space*: the field laid out
 * unturned, which the renderer draws into through `canvasTransform`. At 0 the
 * drawing space is the screen. `toScreen` and `toField` always speak screen.
 */
export interface Viewport {
  /** Top-left of the field box, in drawing-space CSS pixels. */
  readonly originX: number;
  readonly originY: number;
  readonly fieldW: number;
  readonly fieldH: number;
  /** Pixels per field unit for radii, stroke widths and text. */
  readonly unit: number;
  /** The drawing space's size: the canvas's, swapped when turned. */
  readonly canvasW: number;
  readonly canvasH: number;
  readonly rotation: ViewRotation;
}

/**
 * Space to reserve beyond each edge of the field box, as a multiple of the field
 * box itself: `left: 0.08` reserves 8% of the field's width outside the left
 * goal line. Already converted to field units — the surface declares its
 * surround in metres, and the renderer does that conversion, so this module
 * stays sport-agnostic arithmetic.
 */
export interface FieldMargins {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

export const NO_MARGINS: FieldMargins = { left: 0, right: 0, top: 0, bottom: 0 };

/** A stretch of the field's length, in field units: `{ from: 0, to: 1 }` is all of it. */
export interface LengthWindow {
  readonly from: number;
  readonly to: number;
}

/**
 * Crops the length to `window` by turning each cropped side into a negative
 * margin — the fitted outer box then covers only the window, and the field box
 * runs past the canvas on that side. A side the window does not crop keeps its
 * margin, so the space behind a goal survives in a half view.
 */
export function cropMargins(margins: FieldMargins, window: LengthWindow): FieldMargins {
  return {
    ...margins,
    left: window.from > 0 ? -window.from : margins.left,
    right: window.to < 1 ? -(1 - window.to) : margins.right,
  };
}

/**
 * Letterboxes the field into the canvas, reserving `margins` around it.
 *
 * The *outer* box — field plus surround — is what gets fitted; the field box is
 * then placed inside it. With zero margins every step below collapses to the
 * plain letterbox, so the default board is unchanged rather than approximately
 * unchanged (`test/viewport.test.ts` pins this against the old algorithm).
 *
 * `Viewport`'s meaning is deliberately unchanged: `toScreen`, `toField` and `px`
 * still map [0,1] onto the *field* box, so hit testing, drag clamping and stored
 * documents never see the margins.
 */
export function computeViewport(
  canvasW: number,
  canvasH: number,
  aspectRatio: number,
  margins: FieldMargins = NO_MARGINS,
  rotation: ViewRotation = 0,
): Viewport {
  // A turned view is fitted into the canvas turned with it.
  if (rotation !== 0) return { ...computeViewport(canvasH, canvasW, aspectRatio, margins), rotation };

  // Room for goals and labels drawn just outside the field box. Stays the floor
  // even when a surround is reserved, since packs may draw beyond what they
  // declare.
  const padding = Math.min(48, Math.max(14, Math.min(canvasW, canvasH) * 0.05));
  const availableW = Math.max(1, canvasW - padding * 2);
  const availableH = Math.max(1, canvasH - padding * 2);

  const spanX = 1 + margins.left + margins.right;
  const spanY = 1 + margins.top + margins.bottom;

  let outerW = availableW;
  let outerH = outerW / ((aspectRatio * spanX) / spanY);
  if (outerH > availableH) {
    outerH = availableH;
    outerW = outerH * ((aspectRatio * spanX) / spanY);
  }

  const fieldW = outerW / spanX;
  const fieldH = outerH / spanY;

  return {
    originX: (canvasW - outerW) / 2 + margins.left * fieldW,
    originY: (canvasH - outerH) / 2 + margins.top * fieldH,
    fieldW,
    fieldH,
    unit: Math.min(fieldW, fieldH),
    canvasW,
    canvasH,
    rotation: 0,
  };
}

/** Field to drawing space: where the renderer draws, under `canvasTransform`. */
export function toCanvas(p: Vec2, vp: Viewport): Vec2 {
  return { x: vp.originX + p.x * vp.fieldW, y: vp.originY + p.y * vp.fieldH };
}

/** Field to screen, turned as the view is — what hit testing measures against. */
export function toScreen(p: Vec2, vp: Viewport): Vec2 {
  const v = toCanvas(p, vp);
  if (vp.rotation === 90) return { x: vp.canvasH - v.y, y: v.x };
  if (vp.rotation === -90) return { x: v.y, y: vp.canvasW - v.x };
  return v;
}

export function toField(px: number, py: number, vp: Viewport): Vec2 {
  const v =
    vp.rotation === 90
      ? { x: py, y: vp.canvasH - px }
      : vp.rotation === -90
        ? { x: vp.canvasW - py, y: px }
        : { x: px, y: py };
  return { x: (v.x - vp.originX) / vp.fieldW, y: (v.y - vp.originY) / vp.fieldH };
}

/** The `ctx.transform(a, b, c, d, e, f)` that turns drawing space onto the screen. */
export function canvasTransform(vp: Viewport): readonly [number, number, number, number, number, number] {
  if (vp.rotation === 90) return [0, 1, -1, 0, vp.canvasH, 0];
  if (vp.rotation === -90) return [0, -1, 1, 0, 0, vp.canvasW];
  return [1, 0, 0, 1, 0, 0];
}

/** Converts a field-unit length (radius, stroke width, font size) to pixels. */
export function px(fieldUnits: number, vp: Viewport): number {
  return fieldUnits * vp.unit;
}
